import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import { addNotification } from '../store/slices/notificationSlice';
import { AffiliateService } from '../api/generated/services/AffiliateService';
import { ApiError } from '../api/generated/core/ApiError';
import { helper } from '../utils/helper';
import Input from '../components/ui/Input';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { RecaptchaProvider, useRecaptchaToken } from '../hooks/useRecaptcha';

/**
 * Public partner application page (`/partner`, no auth required).
 * Submits to POST /v2/api/affiliate/apply; approval happens via the link in
 * the admin email - nothing else to do on the frontend after submitting.
 */
const PartnerApplyPageInner: React.FC = () => {
  const getRecaptchaToken = useRecaptchaToken();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);

  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof typeof formData, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Prefill the email for logged-in users (same pattern as ContactForm).
  useEffect(() => {
    if (user?.email) {
      setFormData((prev) => ({ ...prev, email: user.email || '' }));
    }
  }, [user?.email]);

  const handleInputChange = (field: keyof typeof formData) => (value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof typeof formData, string>> = {};
    if (!formData.name.trim()) {
      newErrors.name = t('affiliate.apply_name_required');
    }
    const emailError = helper.validateEmail(formData.email);
    if (emailError) {
      newErrors.email = emailError;
    }
    setErrors(newErrors);
    return Object.values(newErrors).every((e) => !e);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    // Unlike auth, the affiliate-apply endpoint keeps accepting a missing
    // token - preserve the old "send null on captcha failure" behavior here.
    let captchaToken: string | null = null;
    try {
      captchaToken = await getRecaptchaToken('partner_apply');
    } catch (err) {
      console.error('reCAPTCHA token fetch failed:', err);
    }

    setSubmitting(true);
    try {
      await AffiliateService.postAppAffiliatePublicApply({
        name: formData.name.trim(),
        companyName: formData.companyName.trim() || null,
        email: formData.email.trim(),
        message: formData.message.trim() || null,
        captchaToken,
      });
      setSubmitted(true);
    } catch (err) {
      // Errors are surfaced through the project-wide toast notifications.
      let message = t('affiliate.apply_error_generic');
      if (err instanceof ApiError) {
        if (err.status === 422) {
          message = t('affiliate.apply_error_captcha');
        } else if (err.status === 429) {
          message = t('affiliate.apply_error_rate_limit');
        } else {
          // 400: validation error or a repeated application with this email.
          const body = helper.parseApiErrorBody(err);
          message = body?.error || t('affiliate.apply_error_generic');
        }
      }
      dispatch(addNotification({
        type: 'error',
        title: t('notification_error'),
        message,
        autoHideDuration: 5000,
      }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="partner-apply-page">
      <Header isLoginHeader={true} showLoginHeader={true} />

      <div className="contact-form partner-apply-form">
        <div className="contact-form-wrapper">
          <div className="contact-form-container">
            <h2 className="contact-form-title">{t('affiliate.apply_title')}</h2>
            <p className="contact-form-description">{t('affiliate.apply_description')}</p>

            {submitted ? (
              <div className="modal-success">
                <div className="modal-success-title">{t('affiliate.apply_success_title')}</div>
                <div className="modal-success-text">{t('affiliate.apply_success_text')}</div>
              </div>
            ) : (
              <form className="contact-form-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <Input
                    id="name"
                    label={t('affiliate.apply_name_label')}
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    error={errors.name}
                  />
                </div>

                <div className="form-group">
                  <Input
                    id="companyName"
                    label={t('affiliate.apply_company_label')}
                    value={formData.companyName}
                    onChange={handleInputChange('companyName')}
                    error={errors.companyName}
                  />
                </div>

                <div className="form-group">
                  <Input
                    id="email"
                    label={t('affiliate.apply_email_label')}
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    error={errors.email}
                  />
                </div>

                <div className="form-group">
                  <div className="form-field">
                    <label htmlFor="message" className="form-label">
                      {t('affiliate.apply_message_label')}
                    </label>
                    <div className="input-container">
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={(e) => handleInputChange('message')(e.target.value)}
                        className="form-textarea"
                        rows={4}
                      />
                    </div>
                  </div>
                </div>

                {/* Participation terms - a separate link right in the form
                    (points to the static block below on this page). */}
                <a className="terms-link" href="https://seller2go.com/partner-terms" target="_blank">
                  {t('affiliate.apply_terms_title')}
                </a>

                <button type="submit" className="contact-form-button" disabled={submitting}>
                  {submitting
                    ? t('affiliate.apply_sending_button')
                    : t('affiliate.apply_submit_button')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

const PartnerApplyPage: React.FC = () => (
  <RecaptchaProvider>
    <PartnerApplyPageInner />
  </RecaptchaProvider>
);

export default PartnerApplyPage;
